import DeleteIcon from "@mui/icons-material/Delete"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import WarningIcon from "@mui/icons-material/Warning"
import { useTheme, CircularProgress, Typography, Stack } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import IconButton from "@mui/material/IconButton"
import { useConfirm } from "material-ui-confirm"
import {
    ChangeEvent,
    Dispatch,
    ReactElement,
    SetStateAction,
    useState,
} from "react"
import { KeyedMutator } from "swr"

import MfaChip from "./mfaChip"
import {
    DNO_ADMIN,
    DNO_REVIEWER,
    IDENTIFIER_TYPE_DNO_TAG,
    INSTALLER_ADMIN,
    LCT_MANU_ADMIN,
} from "../../../constants/roles"
import {
    TT_ADMIN_SWITCH,
    TT_DELETE_USER_BUTTON,
    TT_FALSE_MFA,
    TT_REVIWER_SWITCH,
} from "../../../constants/tooltips"
import { useGetUserData } from "../../../hooks/useGetUserData"
import useAddGroup from "../../../services/middleware/hooks/adminPortal/useAddGroup"
import useDeleteUser from "../../../services/middleware/hooks/adminPortal/useDeleteUser"
import useRemoveGroup from "../../../services/middleware/hooks/adminPortal/useRemoveGroup"
import {
    TUserOutput,
    TUsersOutput,
} from "../../../services/middleware/mappers/adminPortal/activeUsersMapper"
import AppSwitch from "../../buttons/switch/appSwitch"
import AppTooltip from "../../helper/appTooltip"
import DisplayIf from "../../wrappers/displayIf"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

interface IUserRowProps {
    user: TUserOutput
    mutate: KeyedMutator<TUsersOutput>
    filter: string | null
    mode: string
}

/**
 * Displays each user in a single row
 * <UserRow users={user} />
 */
const UserRow = ({
    user,
    mutate,
    filter,
    mode,
}: IUserRowProps): ReactElement => {
    const theme = useTheme()
    const confirm = useConfirm()
    const { deleteUser, isLoading: deleteUserLoading } = useDeleteUser(
        user.userName,
        user.fullName,
        mutate
    )
    const { addGroup, isLoading: addGroupLoading } = useAddGroup(
        user.userName,
        user.fullName,
        mutate
    )
    const { removeGroup, isLoading: removeGroupLoading } = useRemoveGroup(
        user.userName,
        user.fullName,
        mutate
    )
    const adminConfig = (): [boolean, string] => {
        if (mode === LCT_MANU_ADMIN) {
            return [user.isLctManuAdmin, LCT_MANU_ADMIN]
        }
        if (mode === INSTALLER_ADMIN) {
            return [user.isInstallerAdmin, INSTALLER_ADMIN]
        }
        if (mode === DNO_ADMIN) {
            return [user.isDnoAdmin, DNO_ADMIN]
        }
        return [user.isInstallerAdmin, DNO_ADMIN]
    }
    const [isAdmin, setIsAdmin] = useState(adminConfig()[0])
    const [dnoReviewer, setDnoReviewer] = useState(user.isDnoReviewer)
    const { isDnoAdmin, isEnaSysAdmin } = useGetUserData()
    const isFalseMfa = user.mfa === false
    const tableCellSx = { opacity: isFalseMfa ? 0.5 : 1 }

    const handleChange = (
        event: ChangeEvent<HTMLInputElement>,
        setSwitchState: Dispatch<SetStateAction<boolean>>,
        group: string
    ): void => {
        const isChecked = event.target.checked
        const switchStateOnFail = !isChecked
        setSwitchState(isChecked)
        if (isChecked) {
            addGroup(group, () => {
                setSwitchState(switchStateOnFail)
            })
        } else {
            removeGroup(group, () => {
                setSwitchState(switchStateOnFail)
            })
        }
    }

    const deleteConformation = (): void => {
        confirm({
            title: (
                <Typography variant="h6" component="div" align="center">
                    Are you sure?
                </Typography>
            ),
            description: (
                <Typography variant="body1" component="span">
                    <WarningIcon
                        sx={{
                            fontSize: "2.5rem",
                            color: theme.palette.warning.main,
                            verticalAlign: "middle",
                            marginRight: "1rem",
                        }}
                    />
                    {user.fullName} will be permanently deleted and removed
                </Typography>
            ),
        })
            .then(() => {
                deleteUser()
            })
            .catch(() => {
                /* ... */
            })
    }

    const isRowLoading = (): boolean =>
        addGroupLoading || removeGroupLoading || deleteUserLoading

    const isRowDisabled = (): boolean => isRowLoading() || isFalseMfa

    return (
        <AppTooltip title={user.mfa === true ? "" : TT_FALSE_MFA}>
            <AppTableContentRow>
                <AppTableCell sx={tableCellSx}>
                    {isRowLoading() ? (
                        <CircularProgress />
                    ) : (
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.secondary.main,
                            }}
                        >
                            {user.initials}
                        </Avatar>
                    )}
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    <Stack direction="row">
                        <Typography color="text.primary" marginRight={1}>
                            {user.fullName}
                        </Typography>
                        <DisplayIf condition={isAdmin}>
                            <AppTooltip title="This user is an admin">
                                <ShieldOutlinedIcon
                                    sx={{ color: "text.primary" }}
                                />
                            </AppTooltip>
                        </DisplayIf>
                    </Stack>
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    {" "}
                    <Typography color="text.primary">{user.email}</Typography>
                </AppTableCell>
                <AppTableCell sx={tableCellSx} align="center">
                    <MfaChip isFalseMfa={isFalseMfa} />
                </AppTableCell>
                <AppTableCell align="center">
                    <AppTooltip title={TT_ADMIN_SWITCH}>
                        <span>
                            <AppSwitch
                                checked={isAdmin}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>
                                ) =>
                                    handleChange(
                                        event,
                                        setIsAdmin,
                                        adminConfig()[1]
                                    )
                                }
                                disabled={isRowDisabled()}
                            />
                        </span>
                    </AppTooltip>
                </AppTableCell>
                <DisplayIf
                    condition={
                        isDnoAdmin ||
                        (isEnaSysAdmin &&
                            !!filter?.startsWith(IDENTIFIER_TYPE_DNO_TAG))
                    }
                >
                    <AppTableCell align="center">
                        <AppTooltip title={TT_REVIWER_SWITCH}>
                            <span>
                                <AppSwitch
                                    checked={dnoReviewer}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>
                                    ) =>
                                        handleChange(
                                            event,
                                            setDnoReviewer,
                                            DNO_REVIEWER
                                        )
                                    }
                                    disabled={isRowDisabled()}
                                />
                            </span>
                        </AppTooltip>
                    </AppTableCell>
                </DisplayIf>

                <AppTableCell align="center">
                    <AppTooltip title={TT_DELETE_USER_BUTTON}>
                        <IconButton
                            color="error"
                            onClick={deleteConformation}
                            disabled={isRowLoading()}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </AppTooltip>
                </AppTableCell>
            </AppTableContentRow>
        </AppTooltip>
    )
}

export default UserRow
